import React, { useEffect, useRef, useState } from 'react';
import { renderEmailToHtml } from '../../emails/renderEmail';

interface EmailPreviewProps {
  EmailComponent: React.ComponentType<any>;
  props: Record<string, any>;
  className?: string;
}

/**
 * Componente para previsualização de templates de email
 */
const EmailPreview: React.FC<EmailPreviewProps> = ({
  EmailComponent,
  props,
  className = '',
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(600);
  
  // Renderiza o componente para HTML
  const htmlContent = renderEmailToHtml(EmailComponent, props);

  // Ajusta a altura do iframe automaticamente baseado no conteúdo
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const adjustHeight = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          // Aguarda o conteúdo carregar completamente
          setTimeout(() => {
            const body = iframeDoc.body;
            const html = iframeDoc.documentElement;
            
            if (body && html) {
              // Remove qualquer scroll do iframe
              body.style.overflow = 'visible';
              html.style.overflow = 'visible';
              
              // Calcula a altura total do conteúdo
              const height = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight
              );
              
              // Define uma altura mínima, mas sem máxima para permitir emails longos
              const minHeight = 400;
              const finalHeight = Math.max(height + 40, minHeight); // +40px para margem extra
              
              setIframeHeight(finalHeight);
            }
          }, 150);
        }
      } catch (error) {
        // Em caso de erro (cross-origin), usa altura padrão
        console.warn('Não foi possível ajustar altura automaticamente:', error);
        setIframeHeight(600);
      }
    };

    // Ajusta altura quando o iframe carrega
    iframe.onload = adjustHeight;
    
    // Ajusta altura imediatamente se já carregou
    if (iframe.contentDocument?.readyState === 'complete') {
      adjustHeight();
    }

    return () => {
      if (iframe) {
        iframe.onload = null;
      }
    };
  }, [htmlContent]);

  return (
    <div className={`email-preview-container ${className}`}>
      <iframe
        ref={iframeRef}
        srcDoc={htmlContent}
        title="Email Preview"
        className="w-full border-0"
        style={{ 
          height: `${iframeHeight}px`,
          overflow: 'hidden'
        }}
        sandbox="allow-same-origin allow-scripts"
        scrolling="no"
      />
    </div>
  );
};

export default EmailPreview; 